import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import api, { apiErr } from "../lib/api";
import { fmtSigned, fmtMoney, pnlColor } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { X } from "lucide-react";

export default function PositionsPanel({ refreshKey, onChange }) {
  const { t } = useLang();
  const [positions, setPositions] = useState([]);
  const [history, setHistory] = useState([]);

  const load = () => {
    api.get("/positions").then((r) => setPositions(r.data.positions)).catch(() => {});
    api.get("/history").then((r) => setHistory(r.data.trades)).catch(() => {});
  };
  useEffect(() => { load(); }, [refreshKey]);

  const close = async (id) => {
    try {
      const { data } = await api.post(`/positions/${id}/close`);
      toast.success(`${t("close")} ✓ ${fmtSigned(data.closed.pnl)}`);
      load(); onChange?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="border border-[#1E2028] bg-[#0F1015]">
      <Tabs defaultValue="open">
        <div className="border-b border-[#1E2028] px-4 pt-3">
          <TabsList className="bg-transparent gap-4 p-0 h-auto">
            <TabsTrigger value="open" data-testid="tab-open-positions"
              className="data-[state=active]:bg-transparent data-[state=active]:text-[#FACC15] data-[state=active]:border-b-2 data-[state=active]:border-[#FACC15] rounded-none px-0 pb-2 text-[#9CA3AF] text-sm">
              {t("positions")} ({positions.length})
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history"
              className="data-[state=active]:bg-transparent data-[state=active]:text-[#FACC15] data-[state=active]:border-b-2 data-[state=active]:border-[#FACC15] rounded-none px-0 pb-2 text-[#9CA3AF] text-sm">
              {t("history")} ({history.length})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="open" className="m-0">
          {positions.length === 0 ? (
            <Empty text={t("noPositions")} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">
                  <Th>Symbol</Th><Th>{t("direction")}</Th><Th>{t("entry")}</Th><Th>{t("current")}</Th>
                  <Th>{t("lots")}</Th><Th>{t("strategy")}</Th><Th>{t("pnl")}</Th><Th></Th>
                </tr></thead>
                <tbody>
                  {positions.map((p) => (
                    <tr key={p.id} data-testid={`position-${p.id}`} className="border-t border-[#1E2028] hover:bg-[#17181F] transition-colors duration-200">
                      <Td className="font-display font-semibold">{p.symbol}</Td>
                      <Td>
                        <span className={p.direction === "BUY" ? "text-[#10B981]" : "text-[#EF4444]"}>{p.direction}</span>
                        {p.auto && <span className="ml-1.5 text-[9px] font-mono text-[#8B5CF6] border border-[#8B5CF6]/40 px-1">AUTO</span>}
                      </Td>
                      <Td className="font-mono">{p.entry}</Td>
                      <Td className="font-mono">{p.current_price}</Td>
                      <Td className="font-mono">{p.lots}</Td>
                      <Td className="text-[#FACC15] text-xs">{p.strategy}</Td>
                      <Td className={`font-mono ${pnlColor(p.pnl)}`}>{fmtSigned(p.pnl)}</Td>
                      <Td>
                        <button data-testid={`close-position-${p.id}`} onClick={() => close(p.id)}
                          className="p-1 text-[#6B7280] hover:text-[#EF4444] transition-colors duration-200"><X className="w-4 h-4" /></button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="m-0">
          {history.length === 0 ? (
            <Empty text={t("noHistory")} />
          ) : (
            <div className="overflow-x-auto max-h-[360px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0F1015]"><tr className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">
                  <Th>Symbol</Th><Th>{t("direction")}</Th><Th>{t("entry")}</Th><Th>Close</Th>
                  <Th>{t("strategy")}</Th><Th>{t("result")}</Th>
                </tr></thead>
                <tbody>
                  {history.map((tr) => (
                    <tr key={tr.id} className="border-t border-[#1E2028] hover:bg-[#17181F] transition-colors duration-200">
                      <Td className="font-display font-semibold">{tr.symbol}</Td>
                      <Td><span className={tr.direction === "BUY" ? "text-[#10B981]" : "text-[#EF4444]"}>{tr.direction}</span></Td>
                      <Td className="font-mono">{tr.entry}</Td>
                      <Td className="font-mono">{tr.close_price}</Td>
                      <Td className="text-[#FACC15] text-xs">{tr.strategy}</Td>
                      <Td className={`font-mono ${pnlColor(tr.pnl)}`}>{fmtSigned(tr.pnl)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-2 font-mono">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Empty = ({ text }) => <div className="px-4 py-12 text-center text-sm text-[#6B7280] font-mono">{text}</div>;
